import { BISHOP, KING, KNIGHT, PAWN, QUEEN, ROOK } from './board.ts';

/** A step on the board as [files, ranks], written from White's side (ranks grow towards rank 8). */
export type Vec = readonly [number, number];

/**
 * How a piece type moves. Types are global (not per variant) so FEN letters, art and
 * hashing stay stable everywhere. Movement vectors are from White's point of view and are
 * mirrored for Black, which only matters for asymmetric pieces.
 */
export interface PieceDef {
  type: number;
  /** Lowercase FEN/SAN letter. Unique across all piece types. */
  letter: string;
  name: string;
  /** Centipawns, for the AI and material counts. */
  value: number;
  /** Jumps that may move or capture. */
  leaps?: readonly Vec[];
  /** Jumps that may only move to an empty square. */
  leapsMove?: readonly Vec[];
  /** Jumps that may only capture. */
  leapsCapture?: readonly Vec[];
  /** Rays that slide until blocked; may move or capture. */
  slides?: readonly Vec[];
  /** Standard pawn behaviour: pushes, double step, en passant, promotion. */
  pawn?: boolean;
  /** Royal piece: checks, mates and castling revolve around it. */
  royal?: boolean;
  /** Placed without a colour (snail, duck, wall): uncapturable and impassable. */
  neutral?: boolean;
  /**
   * A state of a standard piece (a wounded knight, a frozen queen): SAN uses that piece's
   * letter, the AI its piece-square table and game phase, and the UI its art by default.
   */
  like?: number;
}

export const ORTHOGONAL: readonly Vec[] = [[0, 1], [1, 0], [0, -1], [-1, 0]];
export const DIAGONAL: readonly Vec[] = [[1, 1], [1, -1], [-1, -1], [-1, 1]];
/** King steps in tie-break order: N, NE, E, SE, S, SW, W, NW. */
export const KING_STEPS: readonly Vec[] = [[0, 1], [1, 1], [1, 0], [1, -1], [0, -1], [-1, -1], [-1, 0], [-1, 1]];
export const KNIGHT_JUMPS: readonly Vec[] = [[1, 2], [2, 1], [2, -1], [1, -2], [-1, -2], [-2, -1], [-2, 1], [-1, 2]];

// Fairy piece types. Add new ones at the end; never renumber (games are stored as UCI, but
// piece codes appear in hashes and art).
export const SNAIL = 7;
export const DUCK = 8;
export const WALL = 9;
export const ARCHBISHOP = 10;
export const CHANCELLOR = 11;
export const AMAZON = 12;
export const CAMEL = 13;
/** A queen resting after a move (Cooldown Chess): can't move or attack for 2 more turns, then 1. */
export const FROZEN_QUEEN_2 = 14;
export const FROZEN_QUEEN_1 = 15;
/** Pieces down to their last hit point (HP Chess). They move like the healthy piece. */
export const WOUNDED_PAWN = 16;
export const WOUNDED_KNIGHT = 17;
export const WOUNDED_BISHOP = 18;
export const WOUNDED_ROOK = 19;
export const WOUNDED_QUEEN = 20;
/** A captured piece back from the dead as a pawn for its captor (Zombie Chess). */
export const ZOMBIE_PAWN = 21;
/** A piece you can't see (Masquerade Chess): only ever drawn, never on a real board. */
export const MASKED = 22;

export const PIECE_DEFS: PieceDef[] = [];

export function definePiece(def: PieceDef): PieceDef {
  if (def.type < 1 || def.type > 31) throw new Error(`Piece type ${def.type} out of range`);
  if (PIECE_DEFS[def.type]) throw new Error(`Piece type ${def.type} already defined`);
  if (PIECE_DEFS.some((d) => d && d.letter === def.letter)) throw new Error(`Piece letter ${def.letter} taken`);
  PIECE_DEFS[def.type] = def;
  return def;
}

definePiece({ type: PAWN, letter: 'p', name: 'pawn', value: 100, pawn: true, leapsCapture: [[-1, 1], [1, 1]] });
definePiece({ type: KNIGHT, letter: 'n', name: 'knight', value: 320, leaps: KNIGHT_JUMPS });
definePiece({ type: BISHOP, letter: 'b', name: 'bishop', value: 330, slides: DIAGONAL });
definePiece({ type: ROOK, letter: 'r', name: 'rook', value: 500, slides: ORTHOGONAL });
definePiece({ type: QUEEN, letter: 'q', name: 'queen', value: 900, slides: [...ORTHOGONAL, ...DIAGONAL] });
definePiece({ type: KING, letter: 'k', name: 'king', value: 0, leaps: KING_STEPS, royal: true });

definePiece({ type: SNAIL, letter: 's', name: 'snail', value: 0, neutral: true });
definePiece({ type: DUCK, letter: 'd', name: 'duck', value: 0, neutral: true });
definePiece({ type: WALL, letter: 'w', name: 'wall', value: 0, neutral: true });
definePiece({ type: ARCHBISHOP, letter: 'a', name: 'archbishop', value: 850, slides: DIAGONAL, leaps: KNIGHT_JUMPS });
definePiece({ type: CHANCELLOR, letter: 'c', name: 'chancellor', value: 900, slides: ORTHOGONAL, leaps: KNIGHT_JUMPS });
definePiece({
  type: AMAZON,
  letter: 'm',
  name: 'amazon',
  value: 1200,
  slides: [...ORTHOGONAL, ...DIAGONAL],
  leaps: KNIGHT_JUMPS,
});
definePiece({
  type: CAMEL,
  letter: 'l',
  name: 'camel',
  value: 250,
  leaps: [[1, 3], [3, 1], [3, -1], [1, -3], [-1, -3], [-3, -1], [-3, 1], [-1, 3]],
});

definePiece({ type: FROZEN_QUEEN_2, letter: 'y', name: 'frozen queen', value: 860, like: QUEEN });
definePiece({ type: FROZEN_QUEEN_1, letter: 'z', name: 'frozen queen', value: 880, like: QUEEN });
definePiece({
  type: WOUNDED_PAWN,
  letter: 'o',
  name: 'wounded pawn',
  value: 70,
  pawn: true,
  leapsCapture: [[-1, 1], [1, 1]],
  like: PAWN,
});
definePiece({ type: WOUNDED_KNIGHT, letter: 'j', name: 'wounded knight', value: 200, leaps: KNIGHT_JUMPS, like: KNIGHT });
definePiece({ type: WOUNDED_BISHOP, letter: 'i', name: 'wounded bishop', value: 205, slides: DIAGONAL, like: BISHOP });
definePiece({ type: WOUNDED_ROOK, letter: 't', name: 'wounded rook', value: 310, slides: ORTHOGONAL, like: ROOK });
definePiece({
  type: WOUNDED_QUEEN,
  letter: 'v',
  name: 'wounded queen',
  value: 560,
  slides: [...ORTHOGONAL, ...DIAGONAL],
  like: QUEEN,
});

definePiece({
  type: ZOMBIE_PAWN,
  letter: 'x',
  name: 'zombie pawn',
  value: 100,
  pawn: true,
  leapsCapture: [[-1, 1], [1, 1]],
  like: PAWN,
});

definePiece({ type: MASKED, letter: 'u', name: 'masked piece', value: 0 });

/** The standard piece a type is a state of (itself for everything else). */
export function baseType(type: number): number {
  return PIECE_DEFS[type]?.like ?? type;
}

export function pieceDef(type: number): PieceDef {
  const def = PIECE_DEFS[type];
  if (!def) throw new Error(`Unknown piece type ${type}`);
  return def;
}

export function typeForLetter(letter: string): number {
  const l = letter.toLowerCase();
  const def = PIECE_DEFS.find((d) => d && d.letter === l);
  return def ? def.type : 0;
}
